import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import CalendarEditCell from '../../components/CalendarCards/CalendarEditCell';
import { Stack } from '@mui/system';
import { activateCycle, fetchActualTemplateData, updateTaskDetails } from './service';
import { Box, CircularProgress } from '@mui/material';
import { convertComprehensiveData } from '../../utils/Utils';
import {
  setComprehensiveDataRx,
  updateComprehensiveTaskRx
} from '../../redux/Calendar/calendarSlice';
import { useDispatch, useSelector } from 'react-redux';
import { monthNames, viewAllTaskFilters } from './constants';
import { filterData } from './utils';
import { useOkta } from '../../hooks/oktaHook';
import { fetchCycleTriggerData } from '../CycleTrigger/services';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';

const ComprehensiveView = ({
  bu,
  userEmail,
  role,
  enableEdit,
  filterValue,
  setAssignedDataList
}) => {
  const dispatch = useDispatch();
  const [loading, setIsLoading] = useState(false);
  const [showAddTask, setShowAddTask] = useState([]);
  const [activeCycleObj, setActiveCycleObj] = useState({});
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const { userDetails } = useOkta();
  const comprehensiveTaskDataRx = useSelector((state) => state.calendar.comprehensiveData);
  const cyclesData = useSelector((state) => state?.cycle);
  const { selectedCycleObj } = cyclesData;
  const { search } = useLocation();
  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const selectedCycle = newParams.get('cycleName');
  const { roles, email } = userDetails;

  const getComprehensiveData = useCallback(async () => {
    const payload = {
      buName: bu,
      userEmail: userEmail,
      role,
      tasksList: [],
      cycleId: selectedCycleId
    };
    if (userEmail !== '') {
      try {
        setIsLoading(true);
        const response = await fetchActualTemplateData(payload);
        const assignedToLength = filterData(response, userEmail)?.length > 0;
        setAssignedDataList(assignedToLength);
        const convertedResponse = convertComprehensiveData(response);
        dispatch(setComprehensiveDataRx(convertedResponse));
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching template data:', error);
        setIsLoading(false);
      }
    }
  }, [bu, userEmail, role]);

  const updateTask = useCallback(
    async (taskId, newStatus) => {
      const selectedTask = comprehensiveTaskDataRx?.find((item) => item?.taskId === taskId);
      if (selectedTask) {
        const { month, ...rest } = selectedTask;
        const updatedTask = { ...rest, status: newStatus };
        const payload = {
          buName: bu,
          userName: userEmail,
          tasksList: [updatedTask],
          cycleId: selectedCycleId
        };
        try {
          setIsLoading(true);
          const response = await updateTaskDetails(payload);
          setIsLoading(false);
          return response;
        } catch (error) {
          console.error('Error updating task:', error);
        }
      }
    },
    [comprehensiveTaskDataRx, bu, userEmail]
  );

  useEffect(() => {
    if (bu && selectedCycleId) {
      getComprehensiveData();
      fetchCycleTriggerData(bu).then((res) => {
        setActiveCycleObj(res?.filter((item) => item.activeStatus === true));
      });
    }
  }, [userEmail]);
  // Function to update status based on index
  const handleStatusChange = (taskId, newStatus, task) => {
    //* should update the comprhensive data by taskid
    // making api call
    let isActiveCycleInSteeringPhase =
      selectedCycleId !== activeCycleObj?.[0]?.id && task?.name === 'Steering Reco';

    if (
      selectedCycleObj?.status !== 'Completed' &&
      selectedCycleObj?.status !== 'Locked' &&
      isActiveCycleInSteeringPhase
    ) {
      setShowConfirmationModal(true);
      setTitle('Voting cannot be progressed');
      setMessage(
        `Since the Cycle ‘${selectedCycle}’ is not 'Completed', you cannot proceed with voting.`
      );
    } else {
      if (activeCycleObj?.length > 0 && isActiveCycleInSteeringPhase) {
        setShowConfirmationModal(true);
        setTitle('Voting is already in progress for another Cycle');
        setMessage(
          `Voting Steps are 'In Progress' for Cycle '${selectedCycle}', hence you are not allowed start voting on this Cycle. If you still want to initiate voting for selected cycle, please complete 'Execute Actions' or change 'Steering Reco' to 'Not Started' in Calendar Cadence for Cycle '${activeCycleObj?.[0]?.cycleName}'.`
        );
      } else {
        const result = updateTask(taskId, newStatus);
        result.then(async (res) => {
          if (res == 'success') {
            let status = false;
            let statusCheck = ['In Progress', 'Completed'];
            if (task?.name === 'Steering Reco') {
              if (
                (task?.status === 'In Progress' && newStatus === 'Completed') ||
                (task?.status === 'Completed' && newStatus === 'In Progress')
              ) {
                // No need to fire api here
              } else {
                if (task?.status === 'Not Started' && statusCheck?.includes(newStatus)) {
                  status = true;
                } else if (statusCheck?.includes(task?.status) && newStatus === 'Not Started') {
                  status = false;
                }
                await activateCycle(taskId, bu, selectedCycleId, status).then(() => {
                  getComprehensiveData();
                });
              }
            } else if (task?.name === 'Final Decisions') {
              if (newStatus === 'Completed') {
                await activateCycle(taskId, bu, selectedCycleId, false).then(() => {
                  getComprehensiveData();
                });
              }
            }
            // Update the status in the comprehensive data redux
            dispatch(updateComprehensiveTaskRx({ taskId, status: newStatus }));
            getComprehensiveData();
          }
        });
      }
    }
  };

  const resetConfirmationModalMsg = () => {
    setShowConfirmationModal(false);
    setMessage('');
    setTitle('');
  };

  const calculateMonthsBetween = (start, end) => {
    // Parse the dates in DD/MM/YYYY format
    const [startDay, startMonth, startYear] = start.split('/').map(Number);
    const [endDay, endMonth, endYear] = end.split('/').map(Number);

    // Create Date objects
    const startDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);
    const startMonthName = monthNames[startDate.getMonth()];
    const endMonthName = monthNames[endDate.getMonth()];
    return startMonthName === endMonthName ? startMonthName : `${startMonthName}-${endMonthName}`;
  };

  const filteredComprehensiveData =
    filterValue === viewAllTaskFilters?.[0]
      ? comprehensiveTaskDataRx
      : filterData(comprehensiveTaskDataRx, email);

  return loading ? (
    <Box // Use Box component for styling
      sx={{
        display: 'flex',
        justifyContent: 'center', // Center horizontally
        alignItems: 'center', // Center vertically
        height: '100vh' // Take full viewport height
      }}>
      <CircularProgress />
    </Box>
  ) : (
    <Stack
      style={{
        width: '100%',
        minHeight: '698px',
        background: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column', // Align children vertically
        alignItems: 'center', // Center horizontally
        padding: '20px'
      }}>
      <div
        style={{
          height: '587px',
          overflowY: 'auto',
          overflowX: 'hidden',
          width: '100%',
          scrollbarWidth: 'thin', // For Firefox
          scrollbarColor: 'rgba(0,0,0,0.2) transparent', // For Firefox
          '&::WebkitScrollbar': {
            width: '6px'
          },
          '&::WebkitScrollbarTrack': {
            background: 'transparent'
          },
          '&::WebkitScrollbarThumb': {
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '3px'
          }
        }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column'
          }}>
          {filteredComprehensiveData?.map((task, index) => (
            <CalendarEditCell
              taskId={task?.taskId}
              key={task?.taskId}
              month={calculateMonthsBetween(task?.timeDuration?.start, task?.timeDuration?.end)}
              weekTitle={task?.periodWeeks}
              status={task?.status} // Pass the current status
              setStatus={(newStatus) => handleStatusChange(task?.taskId, newStatus, task)} // Update status on change
              taskTitle={task?.name}
              taskDescription={task?.taskDescription}
              outlook={task?.outlook}
              taskData={task}
              subtaskData={task?.subTasks}
              fetchApi={() => getComprehensiveData()}
              enableEdit={enableEdit}
              userRoles={roles}
              setShowAddTask={(value) => setShowAddTask(value)}
              showAddTask={showAddTask}
              email={email}
            />
          ))}
        </div>
      </div>
      <ConfirmationModal
        open={showConfirmationModal}
        title={title}
        text={message}
        cancelText=""
        SubmitText={'Close'}
        onClose={() => resetConfirmationModalMsg()}
        onSubmit={() => resetConfirmationModalMsg()}
      />
    </Stack>
  );
};

export default ComprehensiveView;
